import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd, PRIMARY_OUTLET, UrlTree } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

export type SupportedLanguage = 'it' | 'en' | 'de' | 'fr';

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  flag: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLanguageSubject: BehaviorSubject<SupportedLanguage>;
  public currentLanguage$: Observable<SupportedLanguage>;
  private isBrowser: boolean;

  public readonly supportedLanguages: LanguageInfo[] = [
    { code: 'it', name: 'Italiano', flag: 'it' },
    { code: 'en', name: 'Inglese', flag: 'en' },
    { code: 'de', name: 'Tedesco', flag: 'de' },
    { code: 'fr', name: 'Francese', flag: 'fr' }
  ];

  private readonly defaultLanguage: SupportedLanguage = 'it';
  private readonly storageKey = 'divinea_language';

  constructor(
    private router: Router,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    // Priorità: URL > default (italiano). Ignora localStorage per evitare conflitti.
    const langFromUrl = this.detectLanguageFromUrl();
    const initialLang = langFromUrl !== null ? langFromUrl : this.defaultLanguage;
    
    this.currentLanguageSubject = new BehaviorSubject<SupportedLanguage>(initialLang);
    this.currentLanguage$ = this.currentLanguageSubject.asObservable();

    // Imposta subito la lingua
    this.translate.use(initialLang);
    this.updateHtmlLang(initialLang);

    // Sincronizza lingua al cambio di route
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const langFromUrl = this.detectLanguageFromUrl();
        // Se URL non ha prefisso lingua → default italiano
        const newLang = langFromUrl !== null ? langFromUrl : this.defaultLanguage;
        
        if (newLang !== this.currentLanguageSubject.value) {
          this.currentLanguageSubject.next(newLang);
          this.translate.use(newLang);
          this.storeLanguage(newLang);
          this.updateHtmlLang(newLang);
        }
      });
  }

  /**
   * Ottiene la lingua corrente
   */
  getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguageSubject.value;
  }

  /**
   * Imposta una nuova lingua
   */
  setLanguage(lang: SupportedLanguage): void {
    if (this.isSupportedLanguage(lang)) {
      this.currentLanguageSubject.next(lang);
      this.translate.use(lang);
      this.storeLanguage(lang);
      this.updateHtmlLang(lang);
      this.navigateToLanguage(lang);
    }
  }

  /**
   * Verifica se una lingua è supportata
   */
  isSupportedLanguage(lang: string): lang is SupportedLanguage {
    return ['it', 'en', 'de', 'fr'].includes(lang);
  }

  /**
   * Ottiene il prefisso URL per la lingua corrente
   * Per italiano restituisce '' (nessun prefisso)
   * Per altre lingue restituisce '/en', '/de', ecc.
   */
  getLanguagePrefix(lang?: SupportedLanguage): string {
    const targetLang = lang || this.getCurrentLanguage();
    return targetLang === 'it' ? '' : `/${targetLang}`;
  }

  /**
   * Rileva la lingua dalla URL corrente
   */
  private detectLanguageFromUrl(): SupportedLanguage | null {
    const url = this.router.url;
    const segments = url.split('/').filter(s => s);
    
    if (segments.length > 0) {
      const firstSegment = segments[0].split('?')[0]; // Rimuove query params
      if (this.isSupportedLanguage(firstSegment)) {
        return firstSegment;
      }
    }
    
    return null;
  }

  /**
   * Naviga alla stessa pagina ma con una lingua diversa
   */
  private navigateToLanguage(lang: SupportedLanguage): void {
    // Dopo il click su PrimeNG Menubar, una navigazione sincrona può essere annullata
    // o non aggiornare la location: eseguiamo al turno successivo (come /en dalla home).
    const run = (): void => {
      const parsed = this.router.parseUrl(this.router.url);
      const pathSegments = this.getPrimaryPathSegments(parsed);
      const withoutLang = this.stripLanguagePrefixFromPath(pathSegments);
      const onHome = this.isHomepagePath(withoutLang);

      if (onHome) {
        const extras: {
          queryParams?: typeof parsed.queryParams;
          fragment?: string;
        } = {};
        if (Object.keys(parsed.queryParams).length > 0) {
          extras.queryParams = parsed.queryParams;
        }
        if (parsed.fragment) {
          extras.fragment = parsed.fragment;
        }
        const tree: UrlTree =
          lang === 'it'
            ? this.router.createUrlTree(['/'], extras)
            : this.router.createUrlTree(['/', lang], extras);
        void this.router.navigateByUrl(tree);
        return;
      }

      const newUrl = this.translateUrl(this.router.url, lang);
      void this.router.navigateByUrl(newUrl);
    };

    if (this.isBrowser) {
      setTimeout(run, 0);
    } else {
      run();
    }
  }

  private getPrimaryPathSegments(tree: UrlTree): string[] {
    const primary = tree.root.children[PRIMARY_OUTLET];
    return primary ? primary.segments.map((s) => s.path) : [];
  }

  private stripLanguagePrefixFromPath(segments: string[]): string[] {
    const copy = [...segments];
    if (copy.length > 0 && this.isSupportedLanguage(copy[0])) {
      copy.shift();
    }
    return copy;
  }

  private isHomepagePath(segments: string[]): boolean {
    return (
      segments.length === 0 ||
      (segments.length === 1 && segments[0] === 'homepage')
    );
  }

  /**
   * Traduce una URL dalla lingua corrente a quella target
   */
  translateUrl(url: string, targetLang: SupportedLanguage): string {
    const [path, queryString] = url.split('?');
    let segments = path.split('/').filter(s => s);

    // Rimuove il prefisso lingua corrente se presente
    if (segments.length > 0 && this.isSupportedLanguage(segments[0])) {
      segments.shift();
    }

    // Homepage: 'homepage' o path vuoto → root della lingua target (stesso formato di /en manuale)
    const isHomepage = segments.length === 0 || (segments.length === 1 && segments[0] === 'homepage');
    const newPrefix = this.getLanguagePrefix(targetLang);
    const newPath = isHomepage
      ? newPrefix || '/'
      : `${newPrefix}/${segments.join('/')}`;

    return queryString ? `${newPath}?${queryString}` : newPath;
  }

  /**
   * Aggiorna l'attributo lang del tag <html>
   */
  private updateHtmlLang(lang: SupportedLanguage): void {
    if (this.isBrowser && typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }

  /**
   * Salva la lingua nel localStorage
   */
  private storeLanguage(lang: SupportedLanguage): void {
    if (!this.isBrowser) {
      return;
    }
    try {
      localStorage.setItem(this.storageKey, lang);
    } catch (e) {
      // Gestisce errori di localStorage
      console.warn('Could not store language preference', e);
    }
  }

  /**
   * Recupera la lingua dal localStorage
   */
  private getStoredLanguage(): SupportedLanguage | null {
    if (!this.isBrowser) {
      return null;
    }
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored && this.isSupportedLanguage(stored)) {
        return stored;
      }
    } catch (e) {
      // Gestisce errori di localStorage
      console.warn('Could not retrieve language preference', e);
    }
    return null;
  }

  /**
   * Ottiene informazioni sulla lingua corrente
   */
  getCurrentLanguageInfo(): LanguageInfo {
    const currentLang = this.getCurrentLanguage();
    return this.supportedLanguages.find(l => l.code === currentLang) || this.supportedLanguages[0];
  }
}
